import { useState, useEffect } from 'react'
import Layout from '../components/layout/Layout'
import AdminDashboard from './AdminDashboard'
import StaffWorkspace from './StaffWorkspace'
import PlatformAdmin from './PlatformAdmin'
import ProfileScreen from './ProfileScreen'
import AgentDashboard from './AgentDashboard'
import StaffProfile from '../components/StaffProfile'

const DEFAULT_PREFS = { country: 'Nigeria', currency: 'NGN', dateFormat: 'DD/MM/YYYY', timezone: 'Africa/Lagos (WAT)' }

export default function DashboardShell({
  nav,
  company,
  user,
  authProfile,
  role,
  profile,
  profileComplete,
  isPremium,
  onSaveProfile,
  onUpgrade,
  country,
  prefs = DEFAULT_PREFS,
  onSavePrefs,
  onAgentRequest,
  demoMode = false,
  onLogout,
  onProfileClick,
  initialTab,
}) {
  // ✅ Determine default tab based on role
  const defaultTab = role === 'staff' ? 'ledger' : role === 'admin' ? 'overview' : 'dashboard'
  
  // ✅ State for active tab
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab) {
      const profileTabs = ['profile', 'staff-profile', 'platform-settings', 'agent-profile']
      if (profileTabs.includes(initialTab)) {
        return initialTab
      }
    }
    return defaultTab
  })

  // ✅ Sync activeTab when initialTab changes
  useEffect(() => {
    if (initialTab) {
      const profileTabs = ['profile', 'staff-profile', 'platform-settings', 'agent-profile']
      if (profileTabs.includes(initialTab)) {
        setActiveTab(initialTab)
      } else {
        setActiveTab(initialTab)
      }
    }
  }, [initialTab])

  const [mobileOpen, setMobileOpen] = useState(false)

  const handleSelect = (route) => {
    setActiveTab(route)
    setMobileOpen(false)
  }

  // ✅ Determine if back button should show
  const showBack = activeTab !== defaultTab && 
                    activeTab !== 'profile' && 
                    activeTab !== 'staff-profile' && 
                    activeTab !== 'platform-settings' &&
                    activeTab !== 'agent-profile'

  // ✅ Build breadcrumbs
  const getBreadcrumbs = () => {
    if (activeTab === 'profile') return [{ label: 'Profile' }]
    if (activeTab === 'staff-profile') return [{ label: 'My Profile' }]
    if (activeTab === 'platform-settings') return [{ label: 'Settings' }]
    if (activeTab === 'agent-profile') return [{ label: 'Profile' }]

    const items = []
    const findItem = (navItems) => {
      for (const item of navItems) {
        if (item.route === activeTab) return item
        if (item.children) {
          const found = findItem(item.children)
          if (found) return found
        }
      }
      return null
    }

    const buildTrail = (items, target) => {
      for (const item of items) {
        if (item.route === target) {
          return [{ label: item.label, route: item.route }]
        }
        if (item.children) {
          const childTrail = buildTrail(item.children, target)
          if (childTrail) {
            return [{ label: item.label, route: item.route }, ...childTrail]
          }
        }
      }
      return null
    }

    const trail = buildTrail(nav, activeTab)
    if (trail) {
      const last = trail[trail.length - 1]
      if (last) last.route = undefined
      return trail
    }
    return []
  }

  const breadcrumbs = getBreadcrumbs()

  const getPageTitle = () => {
    if (activeTab === 'profile') return 'Profile'
    if (activeTab === 'staff-profile') return 'My Profile'
    if (activeTab === 'platform-settings') return 'Settings'
    if (activeTab === 'agent-profile') return 'Profile'
    
    const findLabel = (items) => {
      for (const item of items) {
        if (item.route === activeTab) return item.label
        if (item.children) {
          const found = findLabel(item.children)
          if (found) return found
        }
      }
      return null
    }
    return findLabel(nav) || 'Dashboard'
  }

  // ============================================================
  // ✅ RENDER CONTENT - EACH ROLE GETS ITS OWN PROFILE PAGE
  // ============================================================
  const renderContent = () => {
    // ============================================================
    // 1. FOUNDER → ProfileScreen (KYC + Business + Directors)
    // ============================================================
    if (role === 'founder' && activeTab === 'profile') {
      return (
        <div className="p-4 sm:p-8 lg:p-10">
          <ProfileScreen
            profile={profile}
            onSave={onSaveProfile}
            companyId={company?.id}
            userId={user?.id}
            onNavigate={handleSelect}
          />
        </div>
      )
    }

    // ============================================================
    // 2. STAFF → StaffProfile (Name + Phone ONLY)
    // ============================================================
    if (role === 'staff' && activeTab === 'staff-profile') {
      return (
        <div className="p-4 sm:p-8 lg:p-10">
          <div className="max-w-2xl mx-auto">
            <StaffProfile />
          </div>
        </div>
      )
    }

    // ============================================================
    // 3. ADMIN → PlatformAdmin → platform-settings
    // ============================================================
    if (role === 'admin' && activeTab === 'platform-settings') {
      return (
        <div className="p-4 sm:p-8 lg:p-10">
          <PlatformAdmin activeTab="platform-settings" />
        </div>
      )
    }

    // ============================================================
    // 4. AGENT → AgentDashboard → agent-profile
    // ============================================================
    if (role === 'agent' && activeTab === 'agent-profile') {
      return <AgentDashboard activeTab="agent-profile" onNavigate={handleSelect} />
    }

    // ============================================================
    // 5. FOUNDER - Regular dashboard
    // ============================================================
    if (role === 'founder') {
      return (
        <AdminDashboard
          activeTab={activeTab}
          onNavigate={handleSelect}
          profileComplete={profileComplete}
          profile={profile}
          isPremium={isPremium}
          onSaveProfile={onSaveProfile}
          onUpgrade={onUpgrade}
          country={prefs.country || country}
          currency={prefs.currency}
          dateFormat={prefs.dateFormat}
          onAgentRequest={onAgentRequest}
          companyName={company}
          demoMode={demoMode}
          prefs={prefs}
          onSavePrefs={onSavePrefs}
        />
      )
    }

    // ============================================================
    // 6. STAFF - Regular workspace
    // ============================================================
    if (role === 'staff') {
      return (
        <StaffWorkspace
          activeTab={activeTab}
          currency={prefs.currency}
          dateFormat={prefs.dateFormat}
        />
      )
    }

    // ============================================================
    // 7. ADMIN - Regular platform admin
    // ============================================================
    if (role === 'admin') {
      return <PlatformAdmin activeTab={activeTab} />
    }

    // Fallback
    return (
      <div className="p-8 text-center text-muted">
        <p>Role "{role}" not recognized. Please contact support.</p>
      </div>
    )
  }

  return (
    <Layout
      nav={nav}
      activeTab={activeTab}
      onNavigate={handleSelect}
      onMenu={() => setMobileOpen(true)}
      mobileOpen={mobileOpen}
      onCloseMobile={() => setMobileOpen(false)}
      company={company}
      user={user}
      profile={authProfile}
      role={role}
      onLogout={onLogout}
      onProfileClick={onProfileClick}
      headerProps={{
        breadcrumbs: breadcrumbs,
        showSearch: true,
        onBack: showBack ? () => handleSelect(defaultTab) : null,
        pageTitle: getPageTitle(),
      }}
    >
      {renderContent()}
    </Layout>
  )
}